import { useMemo } from "react"
import {
  BarChart3,
  Bell,
  BookOpen,
  BriefcaseBusiness,
  Bot,
  KeyRound,
  LifeBuoy,
  type LucideIcon,
  MessageSquare,
  Send,
  Settings,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { ThemeDropdown } from "@f2e/ui/components/Theme"
import {
  useSidebar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarSeparator,
  SidebarMenuButton,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { UserNav } from "@/components/User"
import { SETTINGS_ENDPOINTS } from "@/constants"
import { useUpdateProfileApi } from "@/hooks/api"
import type { BillingOverview } from "@/hooks/api"
import { hasPermission } from "@/lib/utils"
import { getSupportMailto } from "@/lib/support"
import { Organization, Permission, User } from "@/types"
import { useStore } from "@/store"

import HeadDropdownMenu from "./HeadDropdownMenu"
import SidebarCreditBalance from "./SidebarCreditBalance"

export interface MenuItem {
  title: string
  href?: string
  disabled?: boolean
  icon?: LucideIcon
  hasPermission?: boolean
  isActive?: boolean
}

interface AppSidebarProps {
  user: User
  organization: Organization
  billingOverview?: BillingOverview | null
}

interface SidebarSectionProps {
  isOpen: boolean
  label: string
  items: MenuItem[]
  showCollapsedDivider?: boolean
}

const WORKSPACE_TAB_WHITELIST = [
  "edit-agents",
  "channels",
  "conversations",
  "histories",
  "form-submissions",
  "payments",
  "user-management",
  "api-keys",
  "notifications",
] as const

export const resolveWorkspaceTarget = ({
  pathname,
  search,
  workspaceId,
}: {
  pathname: string
  search: string
  workspaceId: string
}) => {
  const searchParams = new URLSearchParams(search)
  const currentWorkspaceTab = searchParams.get("tab")

  if (pathname.startsWith("/dashboard")) {
    const nextParams = new URLSearchParams(search)
    nextParams.set("wid", workspaceId)
    return `/dashboard?${nextParams.toString()}`
  }

  if (!pathname.startsWith("/workspaces")) {
    return null
  }

  if (
    currentWorkspaceTab &&
    WORKSPACE_TAB_WHITELIST.includes(
      currentWorkspaceTab as (typeof WORKSPACE_TAB_WHITELIST)[number]
    )
  ) {
    const nextParams = new URLSearchParams()
    const normalizedTab =
      currentWorkspaceTab === "histories"
        ? "conversations"
        : currentWorkspaceTab
    nextParams.set("tab", normalizedTab)
    const currentNotificationEvent = searchParams.get("event")
    if (normalizedTab === "notifications" && currentNotificationEvent) {
      nextParams.set("event", currentNotificationEvent)
    }
    return `/workspaces/${workspaceId}?${nextParams.toString()}`
  }

  if (pathname.includes("/histories/")) {
    return `/workspaces/${workspaceId}?tab=conversations`
  }

  if (pathname.includes("/users/")) {
    return `/workspaces/${workspaceId}?tab=user-management`
  }

  if (pathname.includes("/agents/")) {
    return `/workspaces/${workspaceId}?tab=edit-agents`
  }

  return `/workspaces/${workspaceId}?tab=edit-agents`
}

const buildWorkspaceHref = (
  workspaceId: string | undefined,
  tab: (typeof WORKSPACE_TAB_WHITELIST)[number]
) =>
  workspaceId
    ? `/workspaces/${workspaceId}?tab=${tab}`
    : SETTINGS_ENDPOINTS.organizationWorkspaces

const buildDashboardHref = (workspaceId: string | undefined) =>
  workspaceId
    ? `/dashboard?wid=${encodeURIComponent(workspaceId)}`
    : "/dashboard"

const SidebarSection = ({
  isOpen,
  label,
  items,
  showCollapsedDivider = true,
}: SidebarSectionProps) => (
  <SidebarGroup className="px-2 py-1">
    {isOpen ? (
      <SidebarGroupLabel className="h-7 px-2 text-xs font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/55">
        {label}
      </SidebarGroupLabel>
    ) : showCollapsedDivider ? (
      <div
        aria-hidden="true"
        className="mx-auto mb-1.5 mt-1 h-px w-6 rounded-full bg-sidebar-border/70"
      />
    ) : null}
    <SidebarGroupContent className={cn(isOpen ? "mt-1" : "mt-0")}>
      <SidebarMenu className={cn(isOpen ? "gap-1" : "items-center gap-2")}>
        {items.map((item) => {
          if (!item.hasPermission) return null

          return (
            <SidebarMenuItem
              key={item.title}
              className={cn(
                "w-full rounded-md",
                !isOpen && "flex justify-center",
                item.isActive &&
                  "text-sidebar-accent-foreground shadow-[0_1px_2px_hsl(var(--sidebar-foreground)/0.04)]"
              )}
            >
              <SidebarMenuButton
                tooltip={item.title}
                isActive={item.isActive}
                size="lg"
                className={cn(
                  "h-11 text-base text-sidebar-foreground/75 transition-[background-color,color,box-shadow] hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground",
                  isOpen
                    ? "justify-start px-3"
                    : "mx-auto size-10! justify-center p-0!",
                  item.isActive &&
                    "bg-sidebar-accent text-sidebar-accent-foreground shadow-[0_1px_2px_hsl(var(--sidebar-foreground)/0.05)]"
                )}
                asChild
              >
                <Link to={item.href || "/"} className={cn(!isOpen && "px-0")}>
                  {item.icon && (
                    <item.icon
                      className={cn(
                        "size-5 stroke-[2.1]",
                        item.isActive
                          ? "text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/70"
                      )}
                    />
                  )}
                  <span className={cn(isOpen ? "ml-2" : "sr-only")}>
                    {item.title}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>
)

const AppSidebar = ({
  user,
  organization,
  billingOverview,
}: AppSidebarProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [workspaces, updateUserMeProfile] = useStore((s) => [
    s.workspaces,
    s.updateUserMeProfile,
  ])
  const { fetchFn: updateProfileApi } = useUpdateProfileApi()
  const { isMobile, open } = useSidebar()
  const isOpen = isMobile ? true : open

  const joinedWorkspaces = useMemo(() => {
    return [...workspaces]
      .filter((workspace) => workspace.isJoined)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [workspaces])

  const currentWorkspaceId = location.pathname.match(
    /^\/workspaces\/([^/]+)/
  )?.[1]

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const currentWorkspaceTab = searchParams.get("tab")
  const dashboardWorkspaceId = searchParams.get("wid") || undefined

  const fallbackWorkspaceId =
    currentWorkspaceId ||
    dashboardWorkspaceId ||
    user.profile?.defaultWorkspaceId ||
    joinedWorkspaces[0]?.id

  const selectedWorkspace = useMemo(() => {
    return (
      joinedWorkspaces.find(
        (workspace) => workspace.id === fallbackWorkspaceId
      ) || joinedWorkspaces[0]
    )
  }, [fallbackWorkspaceId, joinedWorkspaces])

  const handleWorkspaceChange = (workspaceId: string) => {
    if (!user.profile) return

    if (workspaceId !== user.profile.defaultWorkspaceId) {
      updateProfileApi({
        jsonBody: {
          default_workspace_id: workspaceId,
        },
      })
      updateUserMeProfile({
        ...user.profile,
        defaultWorkspaceId: workspaceId,
      })
    }

    const target = resolveWorkspaceTarget({
      pathname: location.pathname,
      search: location.search,
      workspaceId,
    })
    if (target) {
      navigate(target)
    }
  }

  const isWorkspaceScopedRoute = /^\/workspaces\/[^/]+/.test(location.pathname)
  const workspaceItems: MenuItem[] = [
    {
      title: t("common.appSidebar.overview"),
      href: buildDashboardHref(selectedWorkspace?.id),
      icon: BarChart3,
      hasPermission: true,
      isActive: location.pathname.startsWith("/dashboard"),
    },
    {
      title: t("common.appSidebar.agents"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "edit-agents"),
      icon: Bot,
      hasPermission: true,
      isActive:
        (location.pathname.includes("/agents/") ||
          (isWorkspaceScopedRoute &&
            (!currentWorkspaceTab || currentWorkspaceTab === "edit-agents"))) &&
        !location.pathname.includes("/histories/"),
    },
    {
      title: t("common.appSidebar.channels"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "channels"),
      icon: Send,
      hasPermission: true,
      isActive: isWorkspaceScopedRoute && currentWorkspaceTab === "channels",
    },
    {
      title: t("common.appSidebar.conversations"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "conversations"),
      icon: MessageSquare,
      hasPermission: true,
      isActive:
        location.pathname.includes("/histories/") ||
        (isWorkspaceScopedRoute &&
          ["conversations", "histories"].includes(currentWorkspaceTab || "")),
    },
    {
      title: t("common.appSidebar.customers"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "user-management"),
      icon: Users,
      hasPermission: true,
      isActive:
        location.pathname.includes("/users/") ||
        (isWorkspaceScopedRoute && currentWorkspaceTab === "user-management"),
    },
    {
      title: t("common.appSidebar.operations"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "form-submissions"),
      icon: BriefcaseBusiness,
      hasPermission: true,
      isActive:
        isWorkspaceScopedRoute &&
        ["form-submissions", "payments"].includes(currentWorkspaceTab || ""),
    },
    {
      title: t("common.appSidebar.apiKeys"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "api-keys"),
      icon: KeyRound,
      hasPermission: true,
      isActive: isWorkspaceScopedRoute && currentWorkspaceTab === "api-keys",
    },
    {
      title: t("common.appSidebar.notifications"),
      href: buildWorkspaceHref(fallbackWorkspaceId, "notifications"),
      icon: Bell,
      hasPermission: true,
      isActive:
        isWorkspaceScopedRoute && currentWorkspaceTab === "notifications",
    },
  ]

  const organizationItems: MenuItem[] = [
    {
      title: t("common.appSidebar.knowledgeBase"),
      href: "/knowledge-base",
      icon: BookOpen,
      hasPermission: hasPermission(
        organization.myPermissions,
        Permission.OrgViewConnection
      ),
      isActive: location.pathname.startsWith("/knowledge-base"),
    },
    {
      title: t("common.appSidebar.settings"),
      href: SETTINGS_ENDPOINTS.organizationGeneral,
      icon: Settings,
      hasPermission: true,
      isActive: location.pathname.startsWith("/settings"),
    },
  ]

  return (
    <Sidebar collapsible="icon" className="border-sidebar-border/60">
      <SidebarHeader className="px-2 pb-3 pt-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <HeadDropdownMenu organization={organization} />
          </SidebarMenuItem>
        </SidebarMenu>

        {isOpen && joinedWorkspaces.length > 0 && (
          <div className="mt-3">
            <Select
              value={selectedWorkspace?.id}
              onValueChange={handleWorkspaceChange}
            >
              <SelectTrigger className="h-10 w-full bg-background/70">
                <SelectValue
                  placeholder={t("common.workspaceSwitcher.placeholder")}
                />
              </SelectTrigger>
              <SelectContent>
                {joinedWorkspaces.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="gap-1 px-0">
        <SidebarSection
          isOpen={isOpen}
          label={t("common.appSidebar.workspaceSection")}
          items={workspaceItems}
          showCollapsedDivider={false}
        />
        <SidebarSection
          isOpen={isOpen}
          label={t("common.appSidebar.organizationSection")}
          items={organizationItems}
        />
      </SidebarContent>

      <SidebarSeparator className="mx-2 bg-sidebar-border/70" />

      <SidebarFooter className="px-2 pb-3 pt-2">
        <SidebarMenu>
          {billingOverview && (
            <SidebarMenuItem className={cn(!isOpen && "flex justify-center")}>
              <SidebarCreditBalance
                overview={billingOverview}
                isOpen={isOpen}
              />
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={t("common.support.contactSupport")}
              size="sm"
              className="text-muted-foreground hover:text-sidebar-accent-foreground"
              asChild
            >
              <a
                href={getSupportMailto("Codeer support request")}
                className={cn(
                  "justify-start",
                  !isOpen && "justify-center px-0"
                )}
              >
                <LifeBuoy className="size-3.5" />
                <span className="ml-1.5">
                  {t("common.support.contactSupport")}
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <div
              className={cn(
                "flex items-center h-full",
                isOpen ? "flex-row" : "flex-col"
              )}
            >
              <UserNav
                className={cn("h-full px-1 flex-auto", !isOpen && "py-2")}
                user={user}
                showProfile={isOpen}
                size={isOpen ? "md" : "sm"}
              />
              <ThemeDropdown
                className={cn(
                  "h-full w-12 flex-none",
                  !isOpen && "flex-auto w-10 px-0 py-2"
                )}
              />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
